import React, { useEffect, useState, useCallback } from 'react';
import debounce from 'lodash/debounce';
import {
  Box, Typography, Chip, Button, Checkbox,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, useTheme, IconButton, Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchMonitoringAlerts,
  updateAlertStatus,
} from '../../../redux/actions/contractAction';

const impactColorMap = {
  action_required: 'error',
  review_recommended: 'warning',
  informational: 'info',
};

const contractStatusConfig = {
  DRAFT: { label: 'Draft', color: '#78716c' },
  IN_REVIEW: { label: 'In Review', color: '#6366f1' },
  PENDING_SIGNATURE: { label: 'Pending Signature', color: '#f59e0b' },
  SIGNED: { label: 'Signed', color: '#10b981' },
};

const RegulatoryAlertsList = ({ searchTerm = '' }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const alerts = useSelector(state => state.contract?.monitoringAlerts || []);
  const loading = useSelector(state => state.contract?.loadingMonitoring || false);

  const [selectedIds, setSelectedIds] = useState([]);

  // Debounced fetch from backend with search param
  const debouncedFetch = useCallback(
    debounce((term) => {
      const params = {};
      if (term && term.trim()) params.search = term.trim();
      dispatch(fetchMonitoringAlerts(params));
    }, 400),
    [dispatch]
  );

  useEffect(() => {
    debouncedFetch(searchTerm);
  }, [searchTerm, debouncedFetch]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try { return new Date(dateStr).toLocaleDateString(); }
    catch { return dateStr; }
  };

  // All filtering is done by the backend
  const filtered = alerts;

  const handleBulkReview = async () => {
    try {
      await Promise.all(selectedIds.map(id => dispatch(updateAlertStatus(id, 'reviewed'))));
      setSelectedIds([]);
    } catch (err) {
      console.error('Bulk review error:', err);
    }
  };

  const handleReviewContract = (alert) => {
    navigate(`/contract/${alert.contract_id}?alertId=${alert.id}`);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Bulk action bar */}
      {selectedIds.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
            onClick={handleBulkReview}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
          >
            {t('monitoring.bulkReview', 'Mark as Reviewed')} ({selectedIds.length})
          </Button>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.85rem' }}>
            {t('monitoring.noAlerts', 'No regulatory alerts')}
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={{
          borderRadius: '10px',
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: isDark ? alpha('#fff', 0.02) : '#fff',
        }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selectedIds.length === filtered.length && filtered.length > 0}
                    indeterminate={selectedIds.length > 0 && selectedIds.length < filtered.length}
                    onChange={(e) =>
                      setSelectedIds(e.target.checked ? filtered.map(a => a.id) : [])
                    }
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('monitoring.table.contract', 'Contract')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('monitoring.table.law', 'Law')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('monitoring.table.article', 'Article')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('monitoring.table.contractStatus', 'Contract Status')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('monitoring.table.impact', 'Impact')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('monitoring.table.status', 'Status')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('monitoring.table.detected', 'Detected')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('monitoring.table.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((alert) => {
                const impactColor = impactColorMap[alert.impact_level] || 'info';
                return (
                  <TableRow key={alert.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedIds.includes(alert.id)}
                        onChange={(e) =>
                          setSelectedIds(prev =>
                            e.target.checked ? [...prev, alert.id] : prev.filter(id => id !== alert.id)
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: '0.78rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          color: theme.palette.primary.main,
                          '&:hover': { textDecoration: 'underline' },
                        }}
                        onClick={() => handleReviewContract(alert)}
                      >
                        {alert.contract_title || alert.contract_id}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{alert.doc_number}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{alert.article_ref || '—'}</TableCell>
                    <TableCell>
                      {(() => {
                        const statusConf = contractStatusConfig[alert.contract_status] || { label: alert.contract_status || '—', color: '#78716c' };
                        return (
                          <Chip
                            label={statusConf.label}
                            size="small"
                            sx={{
                              height: 20, fontSize: '0.65rem', fontWeight: 600,
                              backgroundColor: alpha(statusConf.color, 0.12),
                              color: statusConf.color,
                            }}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`keyInfo.regulatoryChanges.${alert.impact_level}`, alert.impact_level)}
                        size="small"
                        sx={{
                          height: 20, fontSize: '0.65rem', fontWeight: 600,
                          backgroundColor: alpha(theme.palette[impactColor].main, 0.12),
                          color: theme.palette[impactColor].main,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={alert.status}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>
                      {formatDate(alert.detected_at)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title={t('monitoring.reviewContract', 'Review Contract')}>
                          <IconButton size="small" onClick={() => handleReviewContract(alert)}>
                            <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        {alert.status !== 'reviewed' && (
                          <Tooltip title={t('keyInfo.regulatoryChanges.markReviewed', 'Mark Reviewed')}>
                            <IconButton
                              size="small"
                              onClick={() => dispatch(updateAlertStatus(alert.id, 'reviewed'))}
                            >
                              <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {alert.status !== 'dismissed' && (
                          <Tooltip title={t('monitoring.dismiss', 'Dismiss')}>
                            <IconButton
                              size="small"
                              onClick={() => dispatch(updateAlertStatus(alert.id, 'dismissed'))}
                            >
                              <BlockOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default RegulatoryAlertsList;
